import { Interval, type IntervalOrigin } from "@digits-graphql/frontend/graphql-bearer"
import { type DigitsLocation } from "@digits-shared/components/Router/DigitsLocation"
import {
  ALL_TIME_INDEX_TOKEN,
  ALL_TIME_YEAR_TOKEN,
  isAllTimeUrlPath,
} from "@digits-shared/helpers/allTimeHelpers"
import dateTimeHelper, {
  maxTimeUrlParam,
  type RouteDefaultIntervalOrigin,
} from "@digits-shared/helpers/dateTimeHelper"
import envHelper from "@digits-shared/helpers/envHelper"

const OPTIONAL_TIME_PARAM_NAMES = new Set(["year", "interval", "index", "intervalCount"])
const PARAM_PREFIX_REGEX = /^@?:/
const GROUP_PARAM_PREFIX_REGEX = /:[^(]+\(/

/**
 * Determines if the given parameters represent explicit time selection (user-selected)
 * vs just passing through the current context origin.
 *
 * Returns false if:
 * - No year/index/intervalCount params are provided
 * - The params match the contextOrigin (just passing through current context)
 *
 * Returns true if params differ from context, indicating intentional selection.
 */
function hasExplicitTimeParams(
  params: RouteParameters,
  contextOrigin: IntervalOrigin | undefined
): boolean {
  const hasTimeParams =
    params.year !== undefined || params.index !== undefined || params.intervalCount !== undefined

  if (!hasTimeParams) return false

  // If params match context origin, they're not "explicit" - just context passthrough
  // Must check year, index, AND intervalCount to detect when user selects a specific
  // period that happens to end at the same point as all-time mode
  if (
    contextOrigin &&
    params.year !== undefined &&
    String(params.year) === String(contextOrigin.year) &&
    String(params.index) === String(contextOrigin.index)
  ) {
    // If intervalCount is explicitly provided and differs from context, it's explicit
    // (e.g., user selected "Dec 2024" with intervalCount=1 while in all-time mode with intervalCount=87)
    if (
      params.intervalCount !== undefined &&
      String(params.intervalCount) !== String(contextOrigin.intervalCount)
    ) {
      return true
    }
    return false
  }

  return true
}

export type IncludedQueryParams = "includeAllQueryParams" | "omitAllQueryParams" | string[]

export type RouteParameters = Record<string, string | boolean | number | null | undefined>

export interface GeneratePathOptions {
  location: DigitsLocation
  currentRoute: RouteForPathGeneration
  parameters?: RouteParameters
  includedQueryParams?: IncludedQueryParams
  excludedQueryParams?: string[]
  contextOrigin?: IntervalOrigin
  /**
   * When set, indicates all-time mode is active with this interval (e.g., "month", "quarter", "year").
   * Used to generate all-time URLs when navigating from routes without time tokens.
   * Derived from allTimeInterval in context - undefined means not in all-time mode.
   */
  preservedInterval?: string
  /**
   * Force exit all-time mode, generating explicit time params even if currently in all-time mode.
   * Use when user explicitly selects a specific time range (e.g., preset selection).
   */
  forceExitAllTime?: boolean
}

export interface RouteForPathGeneration {
  name: string
  parameterizedPath: string
  config: {
    parameterizedPath: string
    timeParameterOptions?: {
      defaultIntervalOrigin?: RouteDefaultIntervalOrigin
      interval?: Interval
      ignoreContextOrigin?: boolean
    }
  }
  getParametersFromPath: (pathname: string) => Record<string, string>
}

/**
 * Generates a path with parameters filled in for a route.
 * Any extra parameters not matching route tokens become query params.
 */
export function generatePath(
  route: RouteForPathGeneration,
  parameters: RouteParameters = {},
  contextOrigin?: IntervalOrigin
): string {
  let parameterValues = { ...parameters }
  let routeWithParams = route.parameterizedPath

  const timeOptions = route.config.timeParameterOptions
  if (timeOptions?.interval) {
    if (!dateTimeHelper.isValidIntervalOriginInRoute(parameterValues)) {
      const defaultOrigin = dateTimeHelper.defaultIntervalOriginFromRoute(
        timeOptions.defaultIntervalOrigin,
        timeOptions.interval
      )

      const intervalOrigin =
        (timeOptions.ignoreContextOrigin ? undefined : contextOrigin) ??
        dateTimeHelper.intervalOriginFromRoute(parameterValues, defaultOrigin, {
          interval: timeOptions.interval,
          defaultIntervalOrigin: timeOptions.defaultIntervalOrigin,
          ignoreContextOrigin: timeOptions.ignoreContextOrigin,
        })

      parameterValues = { ...parameterValues, ...intervalOrigin }
    }

    if (parameterValues.interval) {
      const interval = parameterValues.interval as string
      parameterValues.interval = interval.toLowerCase()
    }
  }

  const unusedParams = { ...parameterValues }

  Object.keys(parameterValues).forEach((token) => {
    const previousRouteWithParams = routeWithParams
    const value = parameterValues[token]
    if (value === undefined || value === null) {
      delete unusedParams[token]
      return
    }

    routeWithParams = routeWithParams.replace(new RegExp(`:${token}[^/]*`), value.toString())

    if (previousRouteWithParams !== routeWithParams) delete unusedParams[token]
  })

  if (Object.keys(unusedParams).length) {
    // Normalize numeric maxTime (e.g., from contextOrigin spread) to its URL-friendly
    // representation ("today" or numeric string) before it becomes a query param.
    if (typeof unusedParams.maxTime === "number") {
      unusedParams.maxTime = maxTimeUrlParam(unusedParams.maxTime)
    }

    routeWithParams = `${routeWithParams}?${new URLSearchParams(
      unusedParams as Record<string, string>
    ).toString()}`
  }

  return routeWithParams
}

/**
 * Generates a path using values from the current route/location to fill in missing parameters.
 * This is the primary path generation function used throughout the app.
 */
export function generatePathFromCurrentRoute(
  route: RouteForPathGeneration,
  options: GeneratePathOptions
): string {
  const {
    location,
    currentRoute,
    parameters = {},
    includedQueryParams = "omitAllQueryParams",
    excludedQueryParams = [],
    contextOrigin,
  } = options

  const currentPath = location.pathname

  // Determine if we're in all-time mode:
  // 1. URL pattern indicates all-time (for routes with time tokens in URL)
  // 2. preservedInterval is set (for routes without time tokens, preserved in context)
  // Skip if forceExitAllTime is set (user explicitly selected a specific time range)
  const isCurrentlyAllTime =
    !options.forceExitAllTime &&
    (isAllTimeUrlPath(currentPath) || options.preservedInterval !== undefined)

  const thisRouteParts = route.parameterizedPath.split("/")
  const thisRouteParamNames = thisRouteParts.flatMap((part) => {
    if (GROUP_PARAM_PREFIX_REGEX.test(part)) {
      const match = part.match(/:([^(]+).*/)?.[1]
      return match ? [match] : []
    }
    return PARAM_PREFIX_REGEX.test(part) ? [part.replace(PARAM_PREFIX_REGEX, "")] : []
  })

  const currentRouteParams = currentRoute.getParametersFromPath(currentPath)
  const normalizedParams = normalizeParams(parameters)

  const inputParamsMap = new Map(Object.entries(normalizedParams))
  const currentRouteParamsMap = new Map(Object.entries(currentRouteParams))

  const queryParams: Record<string, string> = {}
  let nextParams: RouteParameters = {}

  thisRouteParamNames.forEach((thisRouteParamName) => {
    const inputParamValue = inputParamsMap.get(thisRouteParamName)
    inputParamsMap.delete(thisRouteParamName)

    const currentRouteParamValue = currentRouteParamsMap.get(thisRouteParamName)
    currentRouteParamsMap.delete(thisRouteParamName)

    nextParams[thisRouteParamName] =
      inputParamValue || currentRouteParamValue || queryParams[thisRouteParamName]
  })

  if (includedQueryParams !== "omitAllQueryParams") {
    Object.entries(location.queryParams || {}).forEach(([key, value]) => {
      if (Array.isArray(includedQueryParams)) {
        if (includedQueryParams.includes(key)) {
          queryParams[key] = value
        }
      } else if (!excludedQueryParams.includes(key)) {
        queryParams[key] = value
      }
    })
  }

  // Preserve intervalCount only if the destination route has time tokens
  // (routes without time tokens don't need intervalCount)
  const hasTimeTokens = Boolean(route.config.timeParameterOptions?.interval)
  if (hasTimeTokens && !excludedQueryParams.includes("intervalCount")) {
    const intervalCount = location.queryParams?.intervalCount
    if (intervalCount !== undefined) {
      queryParams["intervalCount"] = intervalCount
    }
  }

  if (hasTimeTokens && !excludedQueryParams.includes("maxTime")) {
    const maxTime = location.queryParams?.maxTime
    if (maxTime !== undefined) {
      queryParams["maxTime"] = maxTime
    }
  }

  // Filter remaining input parameters against the excludedQueryParams list before
  // allowing them to become query params.
  excludedQueryParams.forEach((excludedParam) => inputParamsMap.delete(excludedParam))
  inputParamsMap.delete("__typename")

  nextParams = {
    ...nextParams,
    ...queryParams,
    ...Object.fromEntries(inputParamsMap.entries()),
  }

  // Handle all-time mode: use all-time URL tokens when in all-time mode
  // Explicit time params indicate intentional time selection, which exits all-time mode
  // However, if the passed params match the context origin, they're not "explicit" -
  // they're just the current context being passed through (common pattern in components)
  const isExplicitTimeSelection = hasExplicitTimeParams(parameters, contextOrigin)

  if (hasTimeTokens && isCurrentlyAllTime && !isExplicitTimeSelection) {
    // Determine interval priority:
    // 1. Current route's interval from URL params
    // 2. Preserved interval from context (when navigating from route without time tokens)
    // 3. Target route's default interval
    // 4. Fall back to "month"
    const currentInterval = currentRouteParams.interval || nextParams.interval
    const targetRouteInterval = route.config.timeParameterOptions?.interval
    const intervalToUse =
      currentInterval ||
      options.preservedInterval ||
      (targetRouteInterval || Interval.Month).toLowerCase()

    nextParams.year = ALL_TIME_YEAR_TOKEN
    nextParams.index = ALL_TIME_INDEX_TOKEN
    nextParams.interval = intervalToUse
    delete nextParams.intervalCount
  }

  const missingDestParamNames = Object.entries(nextParams)
    .filter(([key, _]) => !OPTIONAL_TIME_PARAM_NAMES.has(key))
    .filter(([_, value]) => value === undefined)
    .map(([key, _]) => key)

  if (missingDestParamNames.length) {
    const msg = `The following parameters were not provided for route "${
      route.name
    }": ${missingDestParamNames.join(", ")}`
    console.error(msg)
    if (envHelper.isDevelopment() && !envHelper.isMocks() && !process.env.CI) {
      throw new Error(msg)
    }
  }

  return generatePath(route, nextParams, contextOrigin)
}

/**
 * Normalizes parameters by converting values to strings.
 * Explicit null values are preserved to allow removing query params (intentional clear).
 * Undefined values are filtered out (omitted/not provided).
 */
export function normalizeParams(
  parameters: Record<string, string | number | boolean | undefined | null>
): Record<string, string | null> {
  return Object.fromEntries(
    Object.entries(parameters)
      .filter(isDefined)
      .map(([key, value]) => {
        if (key === "maxTime" && typeof value === "number") return [key, maxTimeUrlParam(value)]
        return [key, value === null ? null : value.toString()]
      })
  )
}

/**
 * Generates a Route path pattern by replacing parameterized segments with specific values.
 * This is useful for React Router Route components that need to match specific parameter values.
 *
 * Example:
 *   route.parameterizedPath = "/financials/:kind(ProfitAndLoss|BalanceSheet|CashFlow)"
 *   generateRoutePathPattern(route, { kind: "ProfitAndLoss" })
 *   => "/financials/:kind(ProfitAndLoss)"
 *
 * @param route - The route to generate a path pattern for
 * @param parameters - Object with parameter names and values to replace in the pattern
 * @returns A path string that can be used in React Router's Route component path prop
 */
export function generateRoutePathPattern(
  route: RouteForPathGeneration,
  parameters: Record<string, string | number>
): string {
  let pattern = route.parameterizedPath

  // Replace each parameter in the pattern with its specific value
  Object.entries(parameters).forEach(([paramName, paramValue]) => {
    // Match patterns like :paramName(...) or :paramName
    const paramRegex = new RegExp(`:${paramName}(?:\\([^)]+\\))?`, "g")
    pattern = pattern.replace(paramRegex, `:${paramName}(${paramValue})`)
  })

  return pattern
}

type ParamValue = string | number | boolean | null
function isDefined(
  entry: [string, ParamValue | undefined]
): entry is [string, string | number | boolean] {
  return entry[1] !== undefined
}
